import React, { useEffect, useMemo, useState } from 'react';
import {
  AccountBalanceRounded,
  CreditCardRounded,
  LocalOfferOutlined,
  PhoneIphoneRounded,
  RadioButtonCheckedRounded,
  RadioButtonUncheckedRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { subscriptionMetaData, validatePromoCode, createPaymentIntent, getCountryMetaData } from './login.service';
import { paymentFlowMode, onlinePaymentsOff, resolveAvailablePaymentMethods } from './paymentProviders';
import { normalizePlan } from './signupPlanCatalog';
import signupFlowSteps from './signupFlowConfig';
import SignupProgress from './signupProgress';
import { signupPaymentSchema } from './validationSchemas';
import { authHeadingStyles, authSubtextStyles } from './signupTheme';

const fieldLabelStyles = {
  color: 'text.primary',
  fontWeight: 700,
};

const fieldGroupStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const isDevMockPaymentEnabled =
  process.env.NODE_ENV !== 'production' &&
  (process.env.REACT_APP_ENABLE_DEV_PAYMENT_MOCKS ?? 'true') === 'true';

const formatCurrency = (currency, amount) =>
  `${currency}${Number(amount || 0).toFixed(2)}`;

const getCurrencyCode = (currencySymbol) => {
  const currencyMap = {
    '$': 'USD',
    '€': 'EUR',
    '£': 'GBP',
    '¥': 'JPY',
    '₹': 'INR',
    '₽': 'RUB',
    '¢': 'USD',
  };
  return currencyMap[currencySymbol] || 'USD';
};

const getMethodVisual = (paymentOption) => {
  if (paymentOption?.provider === 'apple_pay') {
    return (
      <Box
        component="img"
        src="/assets/images/apple-pay-logo.svg"
        alt="Apple Pay"
        sx={{ height: 20, width: 'auto' }}
      />
    );
  }

  if (paymentOption?.provider === 'google_pay' || paymentOption?.methodType === 'wallet') {
    return (
      <Box
        component="img"
        src="/assets/images/google-pay-logo.svg"
        alt="Google Pay"
        sx={{ height: 20, width: 'auto' }}
      />
    );
  }

  if (paymentOption?.methodType === 'cash') {
    return <AccountBalanceRounded sx={{ color: 'text.primary' }} />;
  }

  if (paymentOption?.methodType === 'bank') {
    return <AccountBalanceRounded sx={{ color: 'text.primary' }} />;
  }

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          px: 0.75,
          py: 0.25,
          borderRadius: 0.75,
          backgroundColor: '#F5F7FF',
          fontSize: 10,
          fontWeight: 800,
          color: '#1A1F71',
        }}
      >
        VISA
      </Box>
      <Box
        sx={{
          px: 0.75,
          py: 0.25,
          borderRadius: 0.75,
          background: 'linear-gradient(90deg, #EB001B 0%, #F79E1B 100%)',
          fontSize: 10,
          fontWeight: 800,
          color: '#ffffff',
        }}
      >
        MC
      </Box>
    </Stack>
  );
};

export default function SignupStep5({
  handleContinue,
  handleBack,
  handleChange,
  formData,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitCount, setSubmitCount] = useState(0);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [planOptions, setPlanOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [promoCode, setPromoCode] = useState(formData.promoCode || '');
  const [promoState, setPromoState] = useState({
    type: formData.promoDiscountAmount ? 'success' : '',
    message: formData.promoDiscountAmount ? 'Promo code applied.' : '',
    amount: Number(formData.promoDiscountAmount || 0),
  });
  const [clientSecret, setClientSecret] = useState(formData.clientSecret || null);
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [paymentIntentError, setPaymentIntentError] = useState(null);
  const [paymentMethodCompatibilityError, setPaymentMethodCompatibilityError] = useState(null);

  const selectedCountry = useMemo(
    () =>
      (countryOptions || []).find(
        (country) => String(country.id) === String(formData.administrativeRegion)
      ) || null,
    [countryOptions, formData.administrativeRegion]
  );

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountryMetaData();
        if (response?.data) {
          setCountryOptions(response.data.map((item) => item.countryManagement));
        }
      } catch (error) {
        setCountryOptions([]);
      }
    };

    loadCountries();
  }, []);

  useEffect(() => {
    setPaymentOptions(resolveAvailablePaymentMethods([]));

    const loadPlans = async () => {
      try {
        const response = await subscriptionMetaData(
          formData.userTypeId,
          formData.administrativeRegion
        );

        if (response?.statusCode === 200 || response?.statusCode === 201) {
          const rawPlans = Array.isArray(response?.data)
            ? response.data
            : response?.data?.subscriptionPlans || response?.data?.plans || [];

          if (rawPlans.length > 0) {
            setPlanOptions(rawPlans.map(normalizePlan));
            return;
          }
        }
      } catch (error) {
        // Ignore and fall back to mock data in development.
      }

      setPlanOptions([]);
    };

    loadPlans();
  }, [formData.administrativeRegion, formData.userTypeId]);

  useEffect(() => {
    if (formData.promoCode && Number(formData.promoDiscountAmount) > 0) {
      setPromoCode(formData.promoCode);
      setPromoState({
        type: 'success',
        message: `${formData.promoCode} applied successfully.`,
        amount: Number(formData.promoDiscountAmount),
      });
    } else if (!formData.promoDiscountAmount || Number(formData.promoDiscountAmount) === 0) {
      setPromoCode('');
      setPromoState({
        type: '',
        message: '',
        amount: 0,
      });
    }
  }, [formData.promoCode, formData.promoDiscountAmount]);

  const availablePaymentOptions = useMemo(
    () =>
      paymentOptions.length
        ? paymentOptions
        : isDevMockPaymentEnabled
          ? resolveAvailablePaymentMethods([])
          : [],
    [paymentOptions]
  );

  const availablePlans = useMemo(
    () => planOptions,
    [planOptions]
  );

  const selectedPlan = useMemo(
    () =>
      availablePlans.find(
        (plan) => String(plan.id) === String(formData.subscriptionPlanId)
      ) || null,
    [availablePlans, formData.subscriptionPlanId]
  );

  const selectedPaymentMethod = useMemo(
    () =>
      availablePaymentOptions.find(
        (method) => String(method.id) === String(formData.preferredPaymentMethodId)
      ) || null,
    [availablePaymentOptions, formData.preferredPaymentMethodId]
  );

  const summary = useMemo(() => {
    const currency = selectedPlan?.displayCurrency || '$';
    const monthlySubscriptionPrice = selectedPlan?.monthlyPrice ?? 0;
    const yearlySubscriptionPrice = selectedPlan?.yearlyPrice ?? selectedPlan?.monthlyPrice ?? 0;

    // For monthly billing with financial support, calculate 12 months (upfront payment)
    // For monthly billing without financial support, calculate 1 month (recurring)
    // For yearly, use yearly price
    const subscriptionPrice =
      formData.subscriptionBillingCycle === 'yearly'
        ? yearlySubscriptionPrice
        : formData.financialSupportRequested
        ? monthlySubscriptionPrice * 12
        : monthlySubscriptionPrice;

    const setupFee =
      formData.subscriptionBillingCycle === 'yearly'
        ? selectedPlan?.yearlySetupFee ?? selectedPlan?.setupFee ?? 0
        : selectedPlan?.setupFee ?? 0;
    const subtotal = Number(subscriptionPrice) + Number(setupFee);

    // Apply financial support coverage first (if applicable)
    let coverageAmount = 0;
    let amountAfterCoverage = subtotal;

    if (formData.financialSupportRequested && formData.financialSupportCoveragePercentage) {
      coverageAmount = (subtotal * formData.financialSupportCoveragePercentage) / 100;
      amountAfterCoverage = subtotal - coverageAmount;
    }

    // Then apply promo discount to the coverage-adjusted amount
    const discountPercentage = promoState.amount || 0;
    const discountAmount = (amountAfterCoverage * discountPercentage) / 100;
    const total = Math.max(0, amountAfterCoverage - discountAmount);

    return {
      currency,
      subscriptionPrice,
      setupFee,
      discountAmount,
      subtotal,
      coverageAmount,
      amountAfterCoverage,
      total,
    };
  }, [formData.subscriptionBillingCycle, promoState.amount, selectedPlan, formData.financialSupportRequested, formData.financialSupportCoveragePercentage]);

  const renderFieldLabel = (label, required = false) => (
    <Typography variant="subtitle2" sx={fieldLabelStyles}>
      {label}
      {required ? (
        <Box component="span" sx={{ color: 'error.main' }}>
          {' '}
          *
        </Box>
      ) : null}
    </Typography>
  );

  const setFieldValue = (name, value, type = 'text') => {
    handleChange({
      target: {
        name,
        value,
        type,
      },
    });
  };

  const clearFieldError = (name) => {
    setErrors((currentErrors) => {
      if (!currentErrors[name]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[name];
      return nextErrors;
    });
  };

  const validateField = async (name, value, overrides = {}) => {
    try {
      await signupPaymentSchema.validateAt(name, {
        ...formData,
        ...overrides,
        [name]: value,
      });
      clearFieldError(name);
    } catch (error) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [name]: error.message,
      }));
    }
  };

  const validateStep = async () => {
    try {
      await signupPaymentSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationError) {
      const nextErrors = {};
      validationError.inner.forEach((error) => {
        if (!nextErrors[error.path]) {
          nextErrors[error.path] = error.message;
        }
      });
      setErrors(nextErrors);
      return false;
    }
  };

  const getCurrencyCode = (currencySymbol) => {
    const currencyMap = {
      '$': 'USD',
      '€': 'EUR',
      '£': 'GBP',
      '¥': 'JPY',
      '₹': 'INR',
      '₽': 'RUB',
      '¢': 'USD',
    };
    return currencyMap[currencySymbol] || 'USD';
  };

  const checkWalletPaymentAvailability = async (provider) => {
    // For wallet payments, check if Payment Request API is available
    if (provider === 'google_pay' || provider === 'apple_pay') {
      if (!stripe) {
        console.warn('Stripe not initialized');
        return false;
      }

      try {
        const paymentRequest = stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'Test',
            amount: 100, // Minimal amount for capability check
          },
          requestPayerEmail: true,
          requestPayerName: true,
        });

        // Check if Payment Request API is available on this device
        const canMakePayment = await paymentRequest.canMakePayment();
        if (!canMakePayment) {
          console.warn(`🔴 Payment Request API not available for ${provider}`);
          return false;
        }

        console.log(`✅ ${provider} is available on this device`);
        return true;
      } catch (error) {
        console.error(`Error checking ${provider} availability:`, error);
        return false;
      }
    }

    // For non-wallet payments, always available
    return true;
  };

  const createPaymentIntentForMethod = async (methodType, isIndia = false) => {
    if (methodType !== 'card' && methodType !== 'wallet') {
      return;
    }

    // Skip PaymentIntent for India card payments (will use Razorpay in Step 6)
    if (isIndia && methodType === 'card') {
      return;
    }

    if (!selectedPlan) {
      setPaymentIntentError('Please select a subscription plan first');
      return;
    }

    setPaymentIntentLoading(true);
    setPaymentIntentError(null);

    try {
      const monthlySubscriptionPrice = selectedPlan?.monthlyPrice ?? 0;
      const yearlySubscriptionPrice = selectedPlan?.yearlyPrice ?? selectedPlan?.monthlyPrice ?? 0;

      // For monthly billing with financial support, calculate 12 months (upfront payment)
      // For monthly billing without financial support, calculate 1 month (recurring)
      // For yearly, use yearly price
      const subscriptionPrice =
        formData.subscriptionBillingCycle === 'yearly'
          ? yearlySubscriptionPrice
          : formData.financialSupportRequested
          ? monthlySubscriptionPrice * 12
          : monthlySubscriptionPrice;

      const setupFee =
        formData.subscriptionBillingCycle === 'yearly'
          ? selectedPlan?.yearlySetupFee ?? selectedPlan?.setupFee ?? 0
          : selectedPlan?.setupFee ?? 0;
      const subtotal = Number(subscriptionPrice) + Number(setupFee);

      // Apply financial support coverage first (if applicable)
      let amountAfterCoverage = subtotal;
      if (formData.financialSupportRequested && formData.financialSupportCoveragePercentage) {
        const coverageAmount = (subtotal * formData.financialSupportCoveragePercentage) / 100;
        amountAfterCoverage = subtotal - coverageAmount;
      }

      // Then apply promo discount to the coverage-adjusted amount
      const discountPercentage = promoState.amount || 0;
      const discountAmount = (amountAfterCoverage * discountPercentage) / 100;
      const total = Math.max(0, amountAfterCoverage - discountAmount);

      // Use the currency code from the subscription plan
      const currencyCode = (selectedPlan?.currencyCode || 'USD').toLowerCase();

      console.log('Creating PaymentIntent for', methodType, 'with amount:', total, currencyCode);

      const response = await createPaymentIntent(total, currencyCode, methodType);

      console.log('createPaymentIntent response:', response);

      if (response?.clientSecret && response?.paymentIntentId) {
        setClientSecret(response.clientSecret);
        setFieldValue('clientSecret', response.clientSecret);
        setFieldValue('paymentIntentId', response.paymentIntentId);
        console.log('✅ PaymentIntent created successfully:', response.paymentIntentId);
      } else {
        setPaymentIntentError('Failed to initialize payment. Please try again.');
      }
    } catch (error) {
      console.error('Error creating PaymentIntent:', error);
      setPaymentIntentError('Failed to initialize payment. Please try again.');
    } finally {
      setPaymentIntentLoading(false);
    }
  };

  const handlePaymentSelect = async (event) => {
    const nextMethod = availablePaymentOptions.find(
      (method) => String(method.id) === String(event.target.value)
    );

    const nextMethodType = nextMethod?.methodType || '';
    const nextProvider = nextMethod?.provider || '';

    setTouched((currentTouched) => ({
      ...currentTouched,
      preferredPaymentMethodId: true,
    }));

    // Check Payment Request API availability for wallet payments
    if (nextMethodType === 'wallet') {
      const isAvailable = await checkWalletPaymentAvailability(nextProvider);
      if (!isAvailable) {
        setPaymentMethodCompatibilityError(
          `${nextMethod?.methodName || 'Apple Pay/Google Pay'} is not available on your device. Please select a debit/credit card instead.`
        );
        setFieldValue('preferredPaymentMethodId', ''); // Clear selection
        setFieldValue('preferredPaymentMethodType', '');
        setFieldValue('preferredPaymentMethodName', '');
        setFieldValue('preferredPaymentProvider', '');
        return;
      }
      setPaymentMethodCompatibilityError(null);
    } else {
      setPaymentMethodCompatibilityError(null);
    }

    setFieldValue('preferredPaymentMethodId', event.target.value);
    setFieldValue('preferredPaymentMethodType', nextMethodType);
    setFieldValue('preferredPaymentMethodName', nextMethod?.methodName || '');
    setFieldValue('preferredPaymentProvider', nextProvider);
    setFieldValue('reviewConfirmed', false, 'checkbox');

    // Reset client secret for non-card/wallet methods
    if (nextMethodType !== 'card' && nextMethodType !== 'wallet') {
      setClientSecret(null);
      setFieldValue('clientSecret', null);
      setFieldValue('paymentIntentId', null);
    }

    await validateField('preferredPaymentMethodId', event.target.value, {
      preferredPaymentMethodType: nextMethodType,
    });

    // Check if this is India card payment
    const isIndiaCardPayment = selectedCountry?.name?.toLowerCase() === 'india' && nextMethodType === 'card';

    // Create PaymentIntent for card and wallet methods (except India cards which use Razorpay)
    if (nextMethodType === 'card' || nextMethodType === 'wallet') {
      await createPaymentIntentForMethod(nextMethodType, isIndiaCardPayment);
    }
  };


  const handleApplyPromo = async () => {
    const normalizedCode = promoCode.trim();

    if (!normalizedCode) {
      setPromoState({
        type: 'error',
        message: 'Enter a promo code to apply it.',
        amount: 0,
      });
      return;
    }

    try {
      const response = await validatePromoCode(normalizedCode);

      if (response?.code && response?.isActive) {
        const discountAmount = Number(response.discountAmount || 0);
        setFieldValue('promoCode', response.code);
        setFieldValue('promoDiscountAmount', discountAmount);
        setPromoState({
          type: 'success',
          message: `${response.code} applied successfully.`,
          amount: discountAmount,
        });
      } else {
        setFieldValue('promoCode', normalizedCode);
        setFieldValue('promoDiscountAmount', 0);
        setPromoState({
          type: 'error',
          message: response?.message || 'This promo code is not valid.',
          amount: 0,
        });
      }
    } catch (error) {
      setFieldValue('promoCode', normalizedCode);
      setFieldValue('promoDiscountAmount', 0);
      setPromoState({
        type: 'error',
        message: error?.message || 'This promo code is not valid.',
        amount: 0,
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitCount(submitCount + 1);
    setTouched({
      preferredPaymentMethodId: true,
    });

    // Check if a payment method is selected
    const selectedMethodId = formData.preferredPaymentMethodId;
    if (!selectedMethodId || selectedMethodId.trim() === '') {
      setErrors({
        preferredPaymentMethodId: 'Select a payment method',
      });
      return;
    }

    const isValid = await validateStep();
    if (!isValid) {
      return;
    }

    // For card method, tokenize the card first (but NOT for India - Razorpay handles it in Step 6)
    if (selectedPaymentMethod && selectedPaymentMethod.methodType === 'card') {
      const isIndiaCard = selectedCountry?.name?.toLowerCase() === 'india';

      console.log('📍 Card payment detected:', {
        country: selectedCountry?.name,
        isIndiaCard,
        hasStripe: !!stripe,
        hasElements: !!elements,
      });

      // Skip tokenization for India cards - Razorpay will handle it in Step 6
      if (!isIndiaCard) {
        if (!stripe || !elements) {
          setErrors({
            preferredPaymentMethodId: 'Stripe is not initialized. Please refresh and try again.',
          });
          return;
        }

        try {
          const cardElement = elements.getElement(CardElement);

          if (!cardElement) {
            console.error('CardElement not found in DOM');
            setErrors({
              preferredPaymentMethodId: 'Card element not found. Please refresh and try again.',
            });
            return;
          }

          const { paymentMethod, error } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
          });

          if (error) {
            setErrors({
              preferredPaymentMethodId: error.message || 'Card validation failed. Please check your card details.',
            });
            return;
          }

          if (paymentMethod) {
            setFieldValue('stripePaymentMethodId', paymentMethod.id);
            console.log('✅ Card tokenized successfully:', paymentMethod.id);
          }
        } catch (error) {
          console.error('Card tokenization error:', error);
          setErrors({
            preferredPaymentMethodId: 'Card tokenization failed. Please try again.',
          });
          return;
        }
      } else {
        console.log('✅ India card payment - skipping Stripe tokenization, will use Razorpay in Step 6');
      }
    }

    // For all payment methods, proceed to step 6
    // Update financial support amounts based on final payment total
    if (formData.financialSupportRequested && summary.coverageAmount > 0) {
      setFieldValue('financialSupportCoverageAmount', summary.coverageAmount);
      setFieldValue('financialSupportUserPaymentAmount', summary.total);

      // Calculate and store grace period dates for monthly subscriptions
      if (formData.subscriptionBillingCycle === 'monthly') {
        const today = new Date();
        const gracePeriodEndDate = new Date(today);
        gracePeriodEndDate.setMonth(gracePeriodEndDate.getMonth() + 6);

        setFieldValue('gracePeriodStartDate', today.toISOString().split('T')[0]);
        setFieldValue('gracePeriodEndDate', gracePeriodEndDate.toISOString().split('T')[0]);
        setFieldValue('gracePeriodMonths', 6);
      }
    }

    setFieldValue('reviewConfirmed', false, 'checkbox');
    handleContinue();
  };

  const subscriptionLabel = selectedPlan
    ? `${selectedPlan.planName} - ${
        formData.subscriptionBillingCycle === 'yearly'
          ? 'Yearly'
          : formData.financialSupportRequested
          ? '12-Month'
          : 'Monthly'
      } Subscription`
    : 'Selected subscription';

  const isCardMethod =
    (selectedPaymentMethod?.methodType || formData.preferredPaymentMethodType) ===
    'card';

  if (formData.isCustomPlanRequest) {
    return (
      <Stack spacing={4} sx={{ maxWidth: 1120, mx: 'auto' }}>
        <Box sx={{ width: '100%', px: { xs: 0, md: 1 }, pb: 1 }}>
          <SignupProgress steps={signupFlowSteps} activeStep={4} />
        </Box>

        <Paper
          elevation={0}
          sx={{
            px: { xs: 2.5, sm: 6, md: 8 },
            py: { xs: 2.5, sm: 6, md: 8 },
            borderRadius: 4,
            backgroundColor: '#ffffff',
            boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
            border: '1px solid #E4E6F1',
          }}
        >
          <Stack spacing={4}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Typography
                variant="h4"
                sx={authHeadingStyles}
              >
                Payment Not Required Yet
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={authSubtextStyles}
              >
                Your custom plan request will be reviewed by our team.
              </Typography>
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                borderColor: '#E4E6F1',
                borderRadius: 3,
                px: { xs: 2.5, sm: 4 },
                py: { xs: 3, sm: 4 },
                backgroundColor: '#FBFCFE',
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  What Happens Next
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, minWidth: 28 }}>
                      1.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Our team will review your request within 24-48 hours
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, minWidth: 28 }}>
                      2.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      We'll contact you to discuss your specific needs and pricing
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, minWidth: 28 }}>
                      3.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Once agreed, you'll receive an invoice for payment
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                pt: 2,
              }}
            >
              <Button
                variant="outlined"
                size="large"
                onClick={handleBack}
                sx={{
                  order: { xs: 2, sm: 1 },
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(90, 143, 17, 0.06)',
                  },
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleContinue}
                sx={{ order: { xs: 1, sm: 2 } }}
              >
                Continue To Review
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={4} sx={{ maxWidth: 1120, mx: 'auto' }}>
      <Box sx={{ width: '100%', px: { xs: 0, md: 1 }, pb: 1 }}>
        <SignupProgress steps={signupFlowSteps} activeStep={4} />
      </Box>

      <Paper
        elevation={0}
        sx={{
          px: { xs: 2.5, sm: 6, md: 8 },
          py: { xs: 2.5, sm: 6, md: 8 },
          borderRadius: 4,
          backgroundColor: '#ffffff',
          boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
          border: '1px solid #E4E6F1',
        }}
      >
          <Stack spacing={4}>
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography
                variant="h4"
                sx={authHeadingStyles}
              >
                Payment Method
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={authSubtextStyles}
              >
                Choose how you want to pay. You&apos;ll review everything before placing
                the order.
              </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 0.9fr' },
              gap: { xs: 3, lg: 5 },
              alignItems: 'start',
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                borderColor: '#E4E6F1',
                borderRadius: 4,
                px: { xs: 3, sm: 4 },
                py: { xs: 3, sm: 4 },
              }}
            >
              <Stack spacing={3}>
                <Typography variant="h5" sx={{ fontSize: 20, fontWeight: 700 }}>
                  Summary
                </Typography>

                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {subscriptionLabel}
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(summary.currency, summary.subscriptionPrice)}
                    </Typography>
                  </Stack>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      One-time Setup Fee
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(summary.currency, summary.setupFee)}
                    </Typography>
                  </Stack>
                </Stack>

                <Stack spacing={1}>
                  <TextField
                    placeholder="Promo code"
                    value={promoCode}
                    onChange={(event) => setPromoCode(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalOfferOutlined sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {promoState.amount > 0 ? (
                            <Button
                              variant="text"
                              onClick={() => {
                                setPromoCode('');
                                setFieldValue('promoCode', '');
                                setFieldValue('promoDiscountAmount', 0);
                                setPromoState({
                                  type: '',
                                  message: '',
                                  amount: 0,
                                });
                              }}
                              sx={{
                                minHeight: 0,
                                px: 0,
                                color: 'error.main',
                                fontWeight: 700,
                                '&:hover': { backgroundColor: 'transparent' },
                              }}
                            >
                              Clear
                            </Button>
                          ) : (
                            <Button
                              variant="text"
                              onClick={handleApplyPromo}
                              sx={{
                                minHeight: 0,
                                px: 0,
                                color: 'primary.main',
                                fontWeight: 700,
                                '&:hover': { backgroundColor: 'transparent' },
                              }}
                            >
                              Apply
                            </Button>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  {promoState.message ? (
                    <Typography
                      variant="caption"
                      color={promoState.type === 'error' ? 'error.main' : 'primary.main'}
                    >
                      {promoState.message}
                    </Typography>
                  ) : null}
                </Stack>

                <Box sx={{ borderTop: '1px solid #E4E6F1', pt: 2 }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Typography variant="body1">Subtotal</Typography>
                      <Typography variant="body1">
                        {formatCurrency(summary.currency, summary.subtotal)}
                      </Typography>
                    </Stack>
                    {summary.coverageAmount > 0 && (
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography variant="body1">
                          Financial Support Coverage
                          {formData.financialSupportCoveragePercentage ? ` (${formData.financialSupportCoveragePercentage}%)` : null}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 700 }}>
                          -{formatCurrency(summary.currency, summary.coverageAmount)}
                        </Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Typography variant="body1">
                        Discount
                        {promoState.amount > 0 ? ` (${promoState.amount}%)` : null}
                      </Typography>
                      <Typography variant="body1">
                        -{formatCurrency(summary.currency, summary.discountAmount)}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        mt: 1,
                        px: 2,
                        py: 1.2,
                        borderRadius: 999,
                        background:
                          'linear-gradient(90deg, rgba(207, 232, 168, 0.85) 0%, rgba(223, 241, 196, 0.95) 100%)',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          TOTAL{formData.financialSupportRequested ? '*' : ''}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {formatCurrency(summary.currency, summary.total)}
                        </Typography>
                      </Stack>
                    </Box>
                    {formData.financialSupportRequested && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        *rest of the amount will be adjusted against the loan amount
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            <Stack spacing={2.5}>
              <Stack spacing={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Select payment method
                  <Box component="span" sx={{ color: 'error.main' }}>
                    {' '}*
                  </Box>
                </Typography>

                <RadioGroup
                  value={formData.preferredPaymentMethodId || ''}
                  onChange={handlePaymentSelect}
                >
                  {availablePaymentOptions.map((paymentOption) => (
                    <FormControlLabel
                      key={paymentOption.id}
                      value={paymentOption.id}
                      control={
                        <Radio
                          icon={<RadioButtonUncheckedRounded />}
                          checkedIcon={
                            <RadioButtonCheckedRounded sx={{ color: 'primary.main' }} />
                          }
                          sx={{ p: 0.5, mr: 1 }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24, overflow: 'visible' }}>
                            {getMethodVisual(paymentOption)}
                          </Box>
                          {paymentOption.provider !== 'apple_pay' &&
                          paymentOption.provider !== 'google_pay' &&
                          paymentOption.methodType !== 'cash' ? (
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                              {paymentOption.methodName}
                            </Typography>
                          ) : null}
                        </Box>
                      }
                      sx={{ m: 0, py: 0.75, alignItems: 'center' }}
                    />
                  ))}
                </RadioGroup>
                {paymentMethodCompatibilityError ? (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {paymentMethodCompatibilityError}
                  </Alert>
                ) : null}
                {(touched.preferredPaymentMethodId || submitCount > 0) && errors.preferredPaymentMethodId ? (
                  <Typography variant="caption" color="error.main">
                    {errors.preferredPaymentMethodId}
                  </Typography>
                ) : null}
              {onlinePaymentsOff ? (
                <Typography variant="caption" color="text.secondary">
                  {availablePaymentOptions.length === 0
                    ? 'Online payment is not available yet. Choose a free plan, or pay another way if your user type offers it.'
                    : 'Online card payment is not available yet. Your request is recorded and payment is arranged with eKutir.'}
                </Typography>
              ) : null}
              {paymentFlowMode === 'mock' && !onlinePaymentsOff ? (
                <Typography variant="caption" color="text.secondary">
                  Wallet and payment authorization will be simulated after review and
                  place order.
                </Typography>
              ) : null}
              </Stack>

              {selectedPaymentMethod && selectedPaymentMethod.methodType === 'card' ? (
                selectedCountry?.name?.toLowerCase() === 'india' ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      borderColor: '#E4E6F1',
                      borderRadius: 3,
                      px: { xs: 2.5, sm: 3 },
                      py: { xs: 2, sm: 2.5 },
                      backgroundColor: '#FBFCFE',
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <CreditCardRounded sx={{ color: 'text.primary' }} />
                      <Typography variant="body2" color="text.secondary">
                        You will enter your card details securely using Razorpay's payment gateway when you review your order.
                      </Typography>
                    </Stack>
                  </Paper>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      borderColor: '#E4E6F1',
                      borderRadius: 3,
                      px: { xs: 2.5, sm: 3 },
                      py: { xs: 2.5, sm: 3 },
                      backgroundColor: '#ffffff',
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Card Details
                      </Typography>
                      <CardElement
                        options={{
                          hidePostalCode: true,
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#424770',
                              '::placeholder': {
                                color: '#aab7c4',
                              },
                            },
                            invalid: {
                              color: '#9e2146',
                            },
                          },
                        }}
                      />
                    </Stack>
                  </Paper>
                )
              ) : selectedPaymentMethod && selectedPaymentMethod.methodType === 'wallet' ? (
                <Paper
                  variant="outlined"
                  sx={{
                    borderColor: '#E4E6F1',
                    borderRadius: 3,
                    px: { xs: 2.5, sm: 3 },
                    py: { xs: 2, sm: 2.5 },
                    backgroundColor: '#FBFCFE',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <PhoneIphoneRounded sx={{ color: 'text.primary' }} />
                    <Typography variant="body2" color="text.secondary">
                      You will complete the payment using {selectedPaymentMethod.methodName} on the next step. Your payment sheet will appear after you review the order.
                    </Typography>
                  </Stack>
                </Paper>
              ) : selectedPaymentMethod ? (
                <Paper
                  variant="outlined"
                  sx={{
                    borderColor: '#E4E6F1',
                    borderRadius: 3,
                    px: { xs: 2.5, sm: 3 },
                    py: { xs: 2, sm: 2.5 },
                    backgroundColor: '#FBFCFE',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <AccountBalanceRounded sx={{ color: 'text.primary' }} />
                    <Typography variant="body2" color="text.secondary">
                      You will finish the purchase using{' '}
                      <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {selectedPaymentMethod.methodName}
                      </Box>{' '}
                      after reviewing your order.
                    </Typography>
                  </Stack>
                </Paper>
              ) : null}

            </Stack>
          </Box>


          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              size="large"
              onClick={handleBack}
              sx={{
                order: { xs: 2, sm: 1 },
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(90, 143, 17, 0.06)',
                },
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              sx={{ order: { xs: 1, sm: 2 } }}
            >
              Continue To Review
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
}
